/**
 * Navegação agrupada por finalidade (redesenho do menu). Apenas reorganiza
 * itens que JÁ existem — as `rota`s apontam para telas reais do sistema; não
 * altera dados nem permissões. Fonte única para a sidebar (desktop) e o hub
 * "Mais" (mobile).
 */
import type { IconName } from "@caint/design-system";
import type { PapelKey } from "@caint/shared";
import { podeGerirCiclo } from "./ciclos.ts";

export interface ItemNav {
  readonly rota: string;
  readonly icone: IconName;
  readonly titulo: string;
  readonly sub: string;
}
export interface GrupoNav {
  readonly id: string;
  readonly nome: string;
  readonly cor: string;
  /** "todos" = todos os papéis; "admin" = gestão (coord/admin/presidência);
   *  "gestao" = gestão + supervisão AUDIN (visão de rede); "ciclo" = só quem gere
   *  ciclos (presidente/admin); "auditoria" = gestão + AUDIN + presidência. */
  readonly papeis: "todos" | "admin" | "gestao" | "ciclo" | "auditoria";
  readonly itens: readonly ItemNav[];
}

export const GRUPOS_NAV: readonly GrupoNav[] = [
  {
    id: "avaliacao",
    nome: "Avaliação",
    cor: "var(--teal-600)",
    papeis: "todos",
    itens: [
      { rota: "inicio", icone: "home", titulo: "Início", sub: "Visão geral da unidade" },
      { rota: "checklist", icone: "check", titulo: "Checklist", sub: "Coleta em campo" },
      { rota: "risco", icone: "risk", titulo: "Riscos", sub: "Matriz e plano de ação" },
    ],
  },
  {
    id: "relatorios",
    nome: "Relatórios",
    cor: "var(--teal-600)",
    papeis: "todos",
    itens: [
      { rota: "relatorio", icone: "report", titulo: "Relatório Executivo", sub: "Consolidação e selo" },
      {
        rota: "relatorio-unidade",
        icone: "doc",
        titulo: "Relatório da Unidade",
        sub: "Relatório Preliminar de Maturidade (PDF)",
      },
    ],
  },
  {
    id: "monitoramento",
    nome: "Monitoramento",
    cor: "var(--coral-500)",
    papeis: "todos",
    itens: [
      {
        rota: "apresentacao",
        icone: "report",
        titulo: "Apresentação do Ciclo",
        sub: "Por que este IMGQ e como subir",
      },
      { rota: "monitoramento", icone: "qaci", titulo: "QACI & IMS", sub: "Pulso mensal entre visitas" },
      { rota: "percepcao", icone: "people", titulo: "Percepção de Risco", sub: "Cultura de risco da equipe" },
      { rota: "imgr", icone: "report", titulo: "IMGR", sub: "Resultado do formulário público" },
      { rota: "supervisao", icone: "audin", titulo: "Supervisão AUDIN", sub: "Asseguração da 3ª linha" },
      { rota: "planos", icone: "check", titulo: "Plano de Ação", sub: "FACR · achados de maturidade 0–2" },
    ],
  },
  {
    id: "rede",
    nome: "Rede",
    cor: "#0E7C86",
    papeis: "gestao",
    itens: [
      {
        rota: "panorama",
        icone: "layers",
        titulo: "Panorama da Rede",
        sub: "Comparativo entre unidades",
      },
    ],
  },
  {
    id: "ciclos",
    nome: "Ciclos",
    cor: "#2D74B8",
    papeis: "ciclo",
    itens: [
      {
        rota: "ciclos",
        icone: "layers",
        titulo: "Ciclos de Avaliação",
        sub: "Abrir, editar, encerrar e escopo",
      },
    ],
  },
  {
    id: "instrumento",
    nome: "Instrumento",
    cor: "#7B4FB0",
    papeis: "admin",
    itens: [
      { rota: "catalogo", icone: "doc", titulo: "Catálogo", sub: "375 itens · perguntas" },
      { rota: "matriz", icone: "risk", titulo: "Matriz de Riscos", sub: "Riscos referenciais" },
      { rota: "motor", icone: "gear", titulo: "Motor de Cálculo", sub: "Parâmetros das fórmulas" },
      { rota: "escalas", icone: "layers", titulo: "Escalas & Selos", sub: "Rubrica e limiares" },
      { rota: "estrutura", icone: "unit", titulo: "Estrutura", sub: "Macroprocessos e setores" },
      { rota: "versoes", icone: "clock", titulo: "Versionamento", sub: "Histórico do instrumento" },
      { rota: "imprimir", icone: "doc", titulo: "Imprimir checklist", sub: "Formulário offline" },
    ],
  },
  {
    id: "migracao",
    nome: "Atualização do Instrumento",
    cor: "#B5651D",
    // Avaliadores DECIDEM; presidente/admin EXECUTA (gating fino na tela). Oculto
    // só para o visualizador (ver `gruposVisiveis`).
    papeis: "todos",
    itens: [
      {
        rota: "migracao",
        icone: "layers",
        titulo: "Atualizar Instrumento",
        sub: "Revisar e aplicar mudanças da planilha oficial",
      },
    ],
  },
  {
    id: "admin",
    nome: "Administração",
    cor: "var(--slate-600)",
    papeis: "admin",
    itens: [
      { rota: "usuarios", icone: "people", titulo: "Usuários & Perfis", sub: "Equipe e permissões" },
      { rota: "matriz-unidade", icone: "unit", titulo: "Matriz da Unidade", sub: "Quadro esperado p/ IMGR" },
    ],
  },
  {
    id: "auditoria",
    nome: "Auditoria",
    cor: "#8A5A00",
    // Gestão + AUDIN (3ª linha de defesa) + presidência.
    papeis: "auditoria",
    itens: [
      {
        rota: "auditoria",
        icone: "audin",
        titulo: "Auditoria",
        sub: "Tudo que muda — quem, o quê e quando",
      },
    ],
  },
];

/** Gestão (vê Instrumento + Administração): coordenação, admin e presidência. */
const PAPEIS_ADMIN = new Set<PapelKey>(["coord", "admin", "presidente"]);
export function papelEhAdmin(papel: PapelKey): boolean {
  return PAPEIS_ADMIN.has(papel);
}

/** Auditoria: gestão + AUDIN (auditoria interna, 3ª linha) — espelha o guard do backend. */
const PAPEIS_AUDITORIA = new Set<PapelKey>(["coord", "admin", "presidente", "audin"]);
export function papelVeAuditoria(papel: PapelKey): boolean {
  return PAPEIS_AUDITORIA.has(papel);
}

/** Gestão da REDE (vê o Panorama comparativo entre unidades): gestão + supervisão
 *  AUDIN. A comparação entre unidades é visão de rede — avaliadores de campo veem
 *  só a própria unidade. */
export function papelEhGestao(papel: PapelKey): boolean {
  return papelEhAdmin(papel) || papel === "audin";
}

/** Visualizador: acompanha todo o sistema, mas não edita nada. */
export function papelSoLeitura(papel: PapelKey): boolean {
  return papel === "visualizador";
}

/** Um grupo é visível ao papel conforme sua faixa de acesso. */
function grupoVisivel(grupo: GrupoNav, papel: PapelKey): boolean {
  if (grupo.papeis === "ciclo") {
    return podeGerirCiclo(papel); // só presidente/admin gerem ciclos
  }
  if (grupo.papeis === "admin") {
    return papelEhAdmin(papel);
  }
  if (grupo.papeis === "gestao") {
    return papelEhGestao(papel); // gestão + AUDIN (visão de rede)
  }
  if (grupo.papeis === "auditoria") {
    return papelVeAuditoria(papel); // gestão + AUDIN
  }
  return true; // "todos"
}

/** Grupos visíveis ao papel (esconde gestão/ciclos dos demais perfis). */
export function gruposVisiveis(grupos: readonly GrupoNav[], papel: PapelKey): GrupoNav[] {
  // Visualizador vê tudo, exceto Administração (usuários), Ciclos (gestão), a
  // Migração de Catálogo e o Panorama da Rede (visão comparativa restrita à
  // gestão/AUDIN — operações restritas a avaliadores/gestão).
  if (papelSoLeitura(papel)) {
    return grupos.filter(
      (g) =>
        g.id !== "admin" &&
        g.id !== "migracao" &&
        g.papeis !== "ciclo" &&
        g.papeis !== "gestao" &&
        g.papeis !== "auditoria",
    );
  }
  return grupos.filter((g) => grupoVisivel(g, papel));
}

/** Filtra os itens pela busca (título + subtítulo); grupos sem resultado somem. */
export function filtrarGrupos(grupos: readonly GrupoNav[], busca: string): GrupoNav[] {
  const q = busca.trim().toLowerCase();
  if (!q) {
    return grupos.map((g) => ({ ...g }));
  }
  return grupos
    .map((g) => ({
      ...g,
      itens: g.itens.filter((i) => i.titulo.toLowerCase().includes(q) || i.sub.toLowerCase().includes(q)),
    }))
    .filter((g) => g.itens.length > 0);
}

/** Iniciais (até 2) de um nome, para avatares. */
export function iniciais(nome: string): string {
  return nome
    .split(" ")
    .map((w) => w[0] ?? "")
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
